'use client'

import React, { useState, useEffect, useRef } from 'react'
import { PhoneOff, Phone, User } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useCallContext } from '@/contexts/CallContext'

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

export function ActiveCallBar() {
  const { t } = useTranslation('callRouting')
  const { activeCall, endCall, holdCall, unholdCall } = useCallContext()
  const [elapsed, setElapsed] = useState(0)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  // Hold-toggling creates a new activeCall object (spread), which must NOT
  // reset the elapsed timer (the effect below keys off callSid, a stable
  // primitive) — but the running setInterval closure still needs the CURRENT
  // onHold value on every tick, hence a ref kept in sync separately.
  const onHoldRef = useRef(false)

  useEffect(() => {
    onHoldRef.current = activeCall?.onHold ?? false
  }, [activeCall?.onHold])

  useEffect(() => {
    setElapsed(0)
    if (timerRef.current) clearInterval(timerRef.current)
    if (!activeCall) return

    timerRef.current = setInterval(() => {
      if (!onHoldRef.current) setElapsed((s) => s + 1)
    }, 1000)

    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
    }
    // Intentionally keyed on callSid, not the whole activeCall object: holding/
    // resuming replaces activeCall with a new object (spread) on every toggle,
    // which would otherwise reset the elapsed timer back to 0 on every hold
    // change. onHoldRef (kept fresh by the effect above) covers the live value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCall?.callSid])

  if (!activeCall) return null

  const displayName = activeCall.callerName || activeCall.callerNumber

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[9998] flex items-center justify-between px-6 py-3 bg-green-700 text-white shadow-2xl">
      {/* Call info */}
      <div className="flex items-center gap-3">
        <div className="relative">
          <div className="h-8 w-8 rounded-full bg-green-600 flex items-center justify-center">
            <Phone className="h-4 w-4" />
          </div>
          <span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-green-300 ring-2 ring-green-700 animate-pulse" />
        </div>

        <div>
          <p className="text-[13px] font-semibold leading-tight">{displayName}</p>
          <p className="text-[11px] text-green-200 leading-tight">
            {activeCall.callerNumber !== displayName ? activeCall.callerNumber : ''}
          </p>
        </div>
      </div>

      {/* Timer */}
      <div className="flex items-center gap-2 text-[15px] font-mono font-semibold tabular-nums">
        <span className="h-2 w-2 rounded-full bg-green-300 animate-pulse" />
        {formatDuration(elapsed)}
      </div>

      {activeCall.onHold && (
        <span className="text-[11px] font-semibold uppercase tracking-wide text-amber-200 bg-amber-800/60 rounded px-2 py-0.5">
          {t('callStatus.onHold')}
        </span>
      )}

      {/* Who is on the call */}
      {activeCall.answeredBy && (
        <div className="hidden sm:flex items-center gap-1.5 text-[12px] text-green-200">
          <User className="h-3.5 w-3.5" />
          <span>{activeCall.answeredBy}</span>
        </div>
      )}

      {/* Hold / Resume */}
      <button
        onClick={() =>
          void (activeCall.onHold ? unholdCall(activeCall.callSid) : holdCall(activeCall.callSid))
        }
        className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[13px] font-medium bg-amber-500 hover:bg-amber-600 transition-colors"
      >
        {activeCall.onHold ? t('callStatus.resume') : t('callStatus.hold')}
      </button>

      {/* End call */}
      <button
        onClick={() => void endCall(activeCall.callSid)}
        className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[13px] font-medium bg-red-500 hover:bg-red-600 transition-colors"
      >
        <PhoneOff className="h-3.5 w-3.5" />
        End Call
      </button>
    </div>
  )
}
